import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchActiveStore,
  openStore,
  closeStore,
  startDiningTable,
  endDiningTable,
  fetchBill,
} from './activeStoreApi';
import type { CheckoutRequestStatus, CheckoutRequestState } from '../../types/order';

// 会計リクエスト状態の型は公開画面と共有するため src/types/order.ts に置き、ここから再エクスポートする（ADR 0068）
export type { CheckoutRequestStatus, CheckoutRequestState };

export interface DiningTable {
  id: string;
  name: string;
}
export interface InActiveDiningTable {
    diningTable: DiningTable;
}

export interface ActiveDiningTable {
  id: string;
  diningTable: DiningTable;
  startTime: string;
  /** 来店人数（開始時入力）。BE ADR 0067 で API に追加。旧 BE レスポンスでは undefined。 */
  numberOfVisitors?: number;
  /** 会計リクエスト状態（BE ADR 0109 / FE ADR 0068）。旧 BE レスポンスでは undefined（NONE 扱い）。 */
  checkoutRequest?: CheckoutRequestState;
}

export interface ActiveStore {
  activeStoreId: string;
  storeId: string;
  numberOfVisitors: number;
  isActive: boolean;
  startTime: string;
  activeTables: ActiveDiningTable[];
  inActiveTables: InActiveDiningTable[];
}

interface ActiveStoreState {
  store: ActiveStore | null;
  loading: boolean;
  error: string | null;
}

const initialState: ActiveStoreState = {
  store: null,
  loading: false,
  error: null,
};

const activeStoreSlice = createSlice({
  name: 'activeStore',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch ActiveStore
      .addCase(fetchActiveStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveStore.fulfilled, (state, action: PayloadAction<ActiveStore | null>) => {
        if (action.payload) {
          state.store = {
            ...action.payload,
            // numberOfVisitors / checkoutRequest などの付加フィールドはスプレッドでそのまま保持する
            activeTables: action.payload.activeTables.map((table) => ({
              ...table,
              diningTable: {
                id: table.diningTable.id,
                name: table.diningTable.name,
              },
            })),
            inActiveTables: action.payload.inActiveTables.map((table) => ({
                diningTable: {
                    id: table.diningTable.id,
                    name: table.diningTable.name,
                }
            })),
          };
        } else {
          state.store = null;
        }
        state.loading = false;
      })
      .addCase(fetchActiveStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Open Store
      .addCase(openStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(openStore.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(openStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Close Store
      .addCase(closeStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(closeStore.fulfilled, (state) => {
        state.store = null; // 閉店後は店舗データをクリア
        state.loading = false;
      })
      .addCase(closeStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Start Dining Table
      .addCase(startDiningTable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startDiningTable.fulfilled, (state, action: PayloadAction<ActiveDiningTable>) => {
        if (state.store) {
          state.store.activeTables.push(action.payload);
          state.store.inActiveTables = state.store.inActiveTables.filter(
            (table) => table.diningTable.id !== action.payload.diningTable.id
          );
        }
        state.loading = false;
      })
      .addCase(startDiningTable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // End Dining Table
      .addCase(endDiningTable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(endDiningTable.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(endDiningTable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Bill
      .addCase(fetchBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBill.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default activeStoreSlice.reducer;